/**
 * TestCaseQuickAdd — inline "new test case" input row.
 *
 * Renders at the bottom of the list. On Enter: POSTs a minimal test case
 * (title only, defaults everything else). On Escape: discards.
 */

import { useState, useRef, useEffect } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { useTestCasesStore } from '../../../store/useTestCasesStore';

const API_URL = API_BASE_URL;

interface TestCaseQuickAddProps {
    projectId:  string;
    folderId?:  string | null;
    onCreated:  (newId: string) => void;
    onCancel:   () => void;
}

export function TestCaseQuickAdd({ projectId, folderId, onCreated, onCancel }: TestCaseQuickAddProps) {
    const { token }  = useAuth();
    const [title, setTitle]   = useState('');
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    async function handleSave() {
        const trimmed = title.trim();
        if (!trimmed) { onCancel(); return; }

        setSaving(true);
        setError(null);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/test-cases`,
                { projectId, title: trimmed, type: 'MANUAL', folderId: folderId ?? undefined },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            onCreated(data.data._id as string);
        } catch (err: unknown) {
            const msg = axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : 'Failed to create';
            setError(msg);
            setSaving(false);
        }
    }

    return (
        <div data-testid="test-case-quick-add" className="flex flex-col border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-gh-bg-dark px-3 py-2">
            <div className="flex items-center gap-2">
                <Plus size={13} className="text-blue-500 flex-shrink-0" />
                <input
                    ref={inputRef}
                    aria-label="Test case title"
                    data-testid="quick-add-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter')  { e.preventDefault(); handleSave(); }
                        if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
                    }}
                    placeholder="Quick add — type title and press Enter"
                    disabled={saving}
                    className="flex-1 text-sm bg-transparent border-none outline-none text-slate-800 dark:text-slate-200 placeholder-slate-400"
                />
                {saving && <Loader2 size={13} className="animate-spin text-slate-400 flex-shrink-0" />}
            </div>
            {error && (
                <p className="text-xs text-rose-500 mt-1 ml-5">{error}</p>
            )}
            <p className="text-[10px] text-slate-400 mt-0.5 ml-5">Enter to save · Esc to cancel</p>
        </div>
    );
}
